import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, RefreshCw, CheckCircle, MessageCircle, Phone } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { supabase } from '../../lib/supabase';
import { NotionVerificationInput } from '../../components/ui/notion/NotionVerificationInput';
import { useExitWarning } from '../../lib/hooks';
import { RegisterSplitLayout } from '../../components/register/RegisterSplitLayout';
import { trackRegisterStepViewed } from '../../lib/analytics/onboardingEvents';
import { REGISTER_VISUALS } from '../../components/register/registerVisuals';
import { VexatiousApprovalNotice } from '../../components/register/VexatiousApprovalNotice';
import { VEXATIOUS_REFS } from '../../lib/config/vexatiousClauses';
import { TERMS_VERSION } from '../../lib/config/termsVersion';

// Dial codes ordered by frequency in IT market: Italy first, then EU neighbors, then rest
const DIAL_CODES: { code: string; flag: string; name: string }[] = [
  { code: '+39', flag: '🇮🇹', name: 'Italia' },
  { code: '+41', flag: '🇨🇭', name: 'Svizzera' },
  { code: '+33', flag: '🇫🇷', name: 'Francia' },
  { code: '+49', flag: '🇩🇪', name: 'Germania' },
  { code: '+34', flag: '🇪🇸', name: 'Spagna' },
  { code: '+44', flag: '🇬🇧', name: 'Regno Unito' },
  { code: '+43', flag: '🇦🇹', name: 'Austria' },
  { code: '+32', flag: '🇧🇪', name: 'Belgio' },
  { code: '+31', flag: '🇳🇱', name: 'Paesi Bassi' },
  { code: '+351', flag: '🇵🇹', name: 'Portogallo' },
  { code: '+30', flag: '🇬🇷', name: 'Grecia' },
  { code: '+353', flag: '🇮🇪', name: 'Irlanda' },
  { code: '+45', flag: '🇩🇰', name: 'Danimarca' },
  { code: '+46', flag: '🇸🇪', name: 'Svezia' },
  { code: '+47', flag: '🇳🇴', name: 'Norvegia' },
  { code: '+358', flag: '🇫🇮', name: 'Finlandia' },
  { code: '+48', flag: '🇵🇱', name: 'Polonia' },
  { code: '+420', flag: '🇨🇿', name: 'Cechia' },
  { code: '+36', flag: '🇭🇺', name: 'Ungheria' },
  { code: '+40', flag: '🇷🇴', name: 'Romania' },
  { code: '+359', flag: '🇧🇬', name: 'Bulgaria' },
  { code: '+385', flag: '🇭🇷', name: 'Croazia' },
  { code: '+386', flag: '🇸🇮', name: 'Slovenia' },
  { code: '+421', flag: '🇸🇰', name: 'Slovacchia' },
  { code: '+356', flag: '🇲🇹', name: 'Malta' },
  { code: '+357', flag: '🇨🇾', name: 'Cipro' },
  { code: '+90', flag: '🇹🇷', name: 'Turchia' },
  { code: '+1', flag: '🇺🇸', name: 'Stati Uniti / Canada' },
  { code: '+52', flag: '🇲🇽', name: 'Messico' },
  { code: '+55', flag: '🇧🇷', name: 'Brasile' },
  { code: '+54', flag: '🇦🇷', name: 'Argentina' },
  { code: '+86', flag: '🇨🇳', name: 'Cina' },
  { code: '+91', flag: '🇮🇳', name: 'India' },
  { code: '+81', flag: '🇯🇵', name: 'Giappone' },
  { code: '+61', flag: '🇦🇺', name: 'Australia' },
  { code: '+971', flag: '🇦🇪', name: 'Emirati Arabi' },
];

// Extract user-friendly error message from a supabase-js invoke error.
// supabase.functions.invoke() returns a FunctionsHttpError with a generic
// "Edge Function returned a non-2xx status code" message on any non-2xx.
// The real reason is in the response body (we set `{ error: '...' }` server-side).
async function extractInvokeError(invokeError: any, fallback: string): Promise<string> {
  if (!invokeError) return fallback;
  if (invokeError.context && typeof invokeError.context.json === 'function') {
    try {
      const body = await invokeError.context.json();
      if (body?.error && typeof body.error === 'string') return body.error;
      if (body?.message && typeof body.message === 'string') return body.message;
    } catch {
      // Body not JSON or already consumed — fall through.
    }
  }
  if (invokeError.message && !String(invokeError.message).includes('non-2xx')) {
    return invokeError.message;
  }
  return fallback;
}

// Match phone string to a known dial code (longest match wins)
function splitDialCode(phone: string): { dialCode: string; local: string } {
  const normalized = phone.replace(/[^\d+]/g, '');
  if (!normalized.startsWith('+')) {
    return { dialCode: '+39', local: normalized };
  }
  // Sort codes by length desc to match longest prefix first (e.g. +351 before +35)
  const sorted = [...DIAL_CODES].sort((a, b) => b.code.length - a.code.length);
  for (const dc of sorted) {
    if (normalized.startsWith(dc.code)) {
      return { dialCode: dc.code, local: normalized.slice(dc.code.length) };
    }
  }
  return { dialCode: '+39', local: normalized.startsWith('+') ? normalized.slice(1) : normalized };
}

export function VerifyEmailPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const sessionToken = searchParams.get('token');

  const [verificationCode, setVerificationCode] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [error, setError] = useState('');
  const [timeLeft, setTimeLeft] = useState(900);
  const [canResend, setCanResend] = useState(false);
  const [sessionStatus, setSessionStatus] = useState<any>(null);
  const [isLoadingStatus, setIsLoadingStatus] = useState(true);

  // WhatsApp fallback state
  const [whatsappAvailable, setWhatsappAvailable] = useState(false);
  const [showWhatsappFallback, setShowWhatsappFallback] = useState(false);
  const [whatsappDialCode, setWhatsappDialCode] = useState('+39');
  const [whatsappPhone, setWhatsappPhone] = useState('');
  const [isSendingWhatsapp, setIsSendingWhatsapp] = useState(false);
  const [whatsappSuccess, setWhatsappSuccess] = useState(false);
  // Cooldown tra due invii WhatsApp consecutivi (anti-spam UX).
  // Si attiva dopo un invio andato a buon fine e blocca il pulsante per 60s.
  const [whatsappCooldownLeft, setWhatsappCooldownLeft] = useState(0);
  // Numero a cui e' stato spedito l'ultimo codice (mostrato nel feedback)
  const [whatsappLastSentTo, setWhatsappLastSentTo] = useState<string | null>(null);

  // Warning on page exit during registration
  useExitWarning(true, 'Sei sicuro di voler abbandonare la registrazione?');

  useEffect(() => { trackRegisterStepViewed('verify'); }, []);

  // Countdown timer
  useEffect(() => {
    if (timeLeft > 0) {
      const timer = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
      return () => clearTimeout(timer);
    } else {
      setCanResend(true);
    }
  }, [timeLeft]);

  // WhatsApp cooldown timer (anti-spam 60s)
  useEffect(() => {
    if (whatsappCooldownLeft <= 0) return;
    const t = setTimeout(() => setWhatsappCooldownLeft((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [whatsappCooldownLeft]);

  // Load session status on mount
  useEffect(() => {
    if (!sessionToken) {
      navigate('/register');
      return;
    }

    const loadSessionStatus = async () => {
      try {
        const { data, error } = await supabase.functions.invoke('get-session-status', {
          body: { sessionToken }
        });

        if (error) {
          console.error('Error loading session status:', error);
          setError('Errore nel caricamento della sessione');
          return;
        }

        if (data.error) {
          console.error('Session status error:', data.error);
          setError(data.error);
          return;
        }

        setSessionStatus(data);

        // Pre-fill WhatsApp phone: split into dial code + local part
        if (data.session?.phone) {
          const { dialCode, local } = splitDialCode(data.session.phone);
          setWhatsappDialCode(dialCode);
          setWhatsappPhone(local);
        }

        // Show WhatsApp fallback only if the transactional WAHA session is WORKING
        setWhatsappAvailable(Boolean(data.channels?.whatsappAvailable));

        if (data.verification.codeValid && data.verification.timeLeft > 0) {
          setTimeLeft(data.verification.timeLeft);
          setCanResend(false);
        } else {
          setTimeLeft(0);
          setCanResend(true);
        }

      } catch (error) {
        console.error('Session status fetch error:', error);
        setError('Errore di connessione');
      } finally {
        setIsLoadingStatus(false);
      }
    };

    loadSessionStatus();
  }, [sessionToken, navigate]);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleVerify = async (code?: string) => {
    const codeToVerify = code || verificationCode;

    if (!codeToVerify.trim()) {
      setError('Inserisci il codice di verifica');
      return;
    }

    if (codeToVerify.length !== 6) {
      setError('Il codice deve essere di 6 cifre');
      return;
    }

    setIsVerifying(true);
    setError('');

    try {
      const { data, error } = await supabase.functions.invoke('verify-email', {
        body: {
          sessionToken,
          verificationCode: codeToVerify,
          // L'inserimento del codice vale come firma elettronica semplice per
          // l'approvazione specifica delle clausole vessatorie (Cass. 20945/2026):
          // la edge function registra l'evidenza (clausole, versione, IP, timestamp).
          vexatiousApproval: {
            termsVersion: TERMS_VERSION,
            clauses: VEXATIOUS_REFS
          }
        }
      });

      if (error) {
        const msg = await extractInvokeError(error, 'Codice non valido. Riprova.');
        throw new Error(msg);
      }

      if (data?.error) {
        throw new Error(data.error);
      }

      // Ripresa di una registrazione a meta': la edge restituisce il token della
      // sessione vera e lo step da cui ripartire (stesse rotte di RegisterPage).
      if (data?.resumeStep && data?.sessionToken) {
        const token = data.sessionToken as string;
        const { data: { session: authSession } } = await supabase.auth.getSession();
        const stepRoutes: Record<number, string> = {
          2: `/register/password?token=${token}`,
          3: authSession ? `/register/profile?token=${token}` : `/register/company?token=${token}`,
          4: `/register/complete?token=${token}`,
          5: `/register/complete?token=${token}`,
        };
        navigate(stepRoutes[data.resumeStep as number] || `/register/password?token=${token}`);
        return;
      }

      navigate(`/register/password?token=${sessionToken}`);

    } catch (error) {
      console.error('Verification error:', error);
      setError(error instanceof Error ? error.message : 'Codice non valido. Riprova.');
    } finally {
      setIsVerifying(false);
    }
  };

  const handleCodeComplete = (code: string) => {
    // Auto-verify when 6 digits entered, but only if code not expired
    if (timeLeft > 0) {
      setTimeout(() => handleVerify(code), 300);
    }
  };

  const handleResendCode = async () => {
    setIsResending(true);
    setError('');

    try {
      const { data, error } = await supabase.functions.invoke('resend-code', {
        body: { sessionToken }
      });

      if (error) {
        const msg = await extractInvokeError(error, "Impossibile inviare un nuovo codice. Riprova tra qualche istante.");
        throw new Error(msg);
      }

      if (data?.error) {
        throw new Error(data.error);
      }

      setTimeLeft(15 * 60);
      setCanResend(false);
      setVerificationCode('');

    } catch (error) {
      console.error('Resend code error:', error);
      setError(error instanceof Error ? error.message : "Impossibile inviare un nuovo codice. Riprova tra qualche istante.");
    } finally {
      setIsResending(false);
    }
  };

  const handleSendWhatsapp = async () => {
    const localDigits = whatsappPhone.replace(/[^\d]/g, '');
    if (!localDigits) {
      setError('Inserisci un numero di telefono');
      return;
    }
    // Basic validation: at least 6 digits (some countries have short national numbers)
    if (localDigits.length < 6) {
      setError('Numero di telefono non valido');
      return;
    }

    const fullPhone = `${whatsappDialCode}${localDigits}`;

    setIsSendingWhatsapp(true);
    setError('');
    setWhatsappSuccess(false);

    try {
      const { data, error } = await supabase.functions.invoke('resend-code', {
        body: {
          sessionToken,
          channel: 'whatsapp',
          phone: fullPhone
        }
      });

      if (error) {
        const msg = await extractInvokeError(error, "Impossibile inviare il codice via WhatsApp. Verifica il numero o riprova tra qualche istante.");
        throw new Error(msg);
      }

      if (data?.error) {
        throw new Error(data.error);
      }

      setTimeLeft(30 * 60); // WhatsApp code validity 30 min
      setCanResend(false);
      setVerificationCode('');
      setWhatsappSuccess(true);
      setWhatsappLastSentTo(fullPhone);
      // Collassa il form input numero per evitare che l'utente lo riveda
      // col pulsante invio gia' abilitato dopo pochi secondi.
      setShowWhatsappFallback(false);
      // Blocca nuovi invii per 60s (anti-spam).
      setWhatsappCooldownLeft(60);

      // Auto-collapse success after a few seconds
      setTimeout(() => setWhatsappSuccess(false), 4000);

    } catch (error) {
      console.error('Send WhatsApp code error:', error);
      setError(error instanceof Error ? error.message : "Impossibile inviare il codice via WhatsApp. Verifica il numero o riprova tra qualche istante.");
    } finally {
      setIsSendingWhatsapp(false);
    }
  };

  if (!sessionToken) {
    return null;
  }

  return (
    <RegisterSplitLayout visual={REGISTER_VISUALS.verify} step={2}>
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-[#37352F] mb-1">
          Verifica la tua email
        </h1>
        <p className="text-[#787774] text-sm">
          Abbiamo inviato un codice di verifica a 6 cifre alla tua email.
        </p>
      </div>

      <form onSubmit={(e) => { e.preventDefault(); handleVerify(); }} className="space-y-6">
        {/* Approvazione specifica clausole vessatorie: va letta PRIMA di digitare
            il codice — l'OTP è la firma elettronica dell'approvazione. */}
        <VexatiousApprovalNotice />

        {/* Verification input */}
        <NotionVerificationInput
          value={verificationCode}
          onChange={setVerificationCode}
          onComplete={handleCodeComplete}
          error={error}
          disabled={isVerifying}
        />

        {/* Timer */}
        <div className="text-center">
          {isLoadingStatus ? (
            <div className="flex items-center justify-center gap-2">
              <div className="animate-spin rounded-full h-4 w-4 border-2 border-[#2383E2] border-t-transparent"></div>
              <span className="text-sm text-[#787774]">Caricamento...</span>
            </div>
          ) : timeLeft > 0 ? (
            <p className="text-sm text-[#787774]">
              Il codice scadrà tra{' '}
              <span className="font-mono font-semibold text-[#37352F]">
                {formatTime(timeLeft)}
              </span>
            </p>
          ) : (
            <p className="text-sm text-red-500">
              Il codice è scaduto
            </p>
          )}
        </div>

        {/* Verify button */}
        <button
          type="submit"
          disabled={isVerifying || verificationCode.length !== 6 || timeLeft === 0}
          className="notion-register-btn w-full"
        >
          {isVerifying ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
              <span>Verifica in corso...</span>
            </>
          ) : (
            <>
              <CheckCircle className="w-4 h-4" />
              <span>Verifica l'email e approva le clausole</span>
            </>
          )}
        </button>

        {/* Resend code */}
        <div className="text-center">
          {canResend ? (
            <motion.button
              initial={{ opacity: 0, y: 5 }}
              animate={{ opacity: 1, y: 0 }}
              type="button"
              onClick={handleResendCode}
              disabled={isResending}
              className="inline-flex items-center gap-1.5 text-[#2383E2] hover:text-[#1a6dc0] text-sm font-medium transition-colors disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${isResending ? 'animate-spin' : ''}`} />
              {isResending ? 'Invio in corso...' : 'Invia nuovo codice'}
            </motion.button>
          ) : (
            <p className="text-sm text-[#787774]">
              Non hai ricevuto il codice? Potrai richiederne uno nuovo quando il timer scadrà.
            </p>
          )}
        </div>

        {/* WhatsApp fallback — visible only when transactional WAHA session is WORKING */}
        {whatsappAvailable && (
        <div className="pt-4 border-t border-[rgba(55,53,47,0.09)] text-center">
          {whatsappSuccess ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="inline-flex flex-col items-center gap-1 px-4 py-2 rounded-lg bg-green-50 border border-green-200 text-green-700 text-sm font-medium"
            >
              <span className="inline-flex items-center gap-2">
                <CheckCircle className="w-4 h-4" />
                Codice inviato su WhatsApp
              </span>
              {whatsappLastSentTo && (
                <span className="text-xs font-normal text-green-600">
                  al numero <span className="font-mono">{whatsappLastSentTo}</span>
                </span>
              )}
            </motion.div>
          ) : !showWhatsappFallback ? (
            whatsappCooldownLeft > 0 ? (
              <div className="inline-flex flex-col items-center gap-0.5">
                <span className="inline-flex items-center gap-1.5 text-[#9B9A97] text-sm font-medium">
                  <MessageCircle className="w-4 h-4" />
                  Riprova fra {whatsappCooldownLeft}s
                </span>
                {whatsappLastSentTo && (
                  <span className="text-[11px] text-[#9B9A97]">
                    inviato a <span className="font-mono">{whatsappLastSentTo}</span>
                  </span>
                )}
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setShowWhatsappFallback(true)}
                className="inline-flex items-center gap-1.5 text-[#787774] hover:text-[#37352F] text-sm font-medium transition-colors"
              >
                <MessageCircle className="w-4 h-4" />
                Non ricevi l'email? Invia il codice via WhatsApp
              </button>
            )
          ) : (
            <AnimatePresence>
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.2 }}
                className="space-y-3 text-left"
              >
                <div>
                  <label htmlFor="whatsapp-phone" className="block text-xs font-medium text-[#787774] mb-1.5">
                    Numero WhatsApp
                  </label>
                  <div className="flex gap-2">
                    <select
                      aria-label="Prefisso internazionale"
                      value={whatsappDialCode}
                      onChange={(e) => setWhatsappDialCode(e.target.value)}
                      disabled={isSendingWhatsapp}
                      className="px-2.5 py-2.5 text-sm bg-white border border-[rgba(55,53,47,0.16)] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2383E2]/30 focus:border-[#2383E2] transition-colors disabled:opacity-50 cursor-pointer min-w-[5.5rem] max-w-[7rem]"
                    >
                      {DIAL_CODES.map((dc) => (
                        <option key={dc.code} value={dc.code}>
                          {dc.flag} {dc.code}
                        </option>
                      ))}
                    </select>
                    <div className="relative flex-1">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9B9A97] pointer-events-none" />
                      <input
                        id="whatsapp-phone"
                        type="tel"
                        inputMode="tel"
                        autoComplete="tel-national"
                        value={whatsappPhone}
                        onChange={(e) => setWhatsappPhone(e.target.value)}
                        disabled={isSendingWhatsapp}
                        placeholder="333 1234567"
                        className="w-full pl-9 pr-3 py-2.5 text-sm bg-white border border-[rgba(55,53,47,0.16)] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2383E2]/30 focus:border-[#2383E2] transition-colors disabled:opacity-50"
                      />
                    </div>
                  </div>
                  <p className="mt-1.5 text-xs text-[#9B9A97]">
                    Inserisci il numero senza prefisso internazionale
                  </p>
                </div>

                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setShowWhatsappFallback(false)}
                    disabled={isSendingWhatsapp}
                    className="flex-1 px-3 py-2 text-sm text-[#787774] hover:bg-[rgba(55,53,47,0.04)] rounded-lg transition-colors disabled:opacity-50"
                  >
                    Annulla
                  </button>
                  <button
                    type="button"
                    onClick={handleSendWhatsapp}
                    disabled={isSendingWhatsapp || !whatsappPhone.trim()}
                    className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-medium text-white bg-[#25D366] hover:bg-[#1DA851] rounded-lg transition-colors disabled:opacity-50"
                  >
                    {isSendingWhatsapp ? (
                      <>
                        <div className="animate-spin rounded-full h-3.5 w-3.5 border-2 border-white border-t-transparent"></div>
                        Invio...
                      </>
                    ) : (
                      <>
                        <MessageCircle className="w-4 h-4" />
                        Invia su WhatsApp
                      </>
                    )}
                  </button>
                </div>
              </motion.div>
            </AnimatePresence>
          )}
        </div>
        )}

        {/* Back button */}
        <div className="pt-4 border-t border-[rgba(55,53,47,0.09)]">
          <button
            type="button"
            onClick={() => navigate('/register')}
            className="w-full flex items-center justify-center gap-2 text-[#787774] hover:text-[#37352F] text-sm font-medium transition-colors py-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Torna alla registrazione
          </button>
        </div>
      </form>
    </RegisterSplitLayout>
  );
}
